import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminBuckets, createAdminBucketStock, updateAdminBucketMeta, deleteAdminBucket } from '../../api/admin';
import { AdminDataTable, AdminSearchBar, AdminPagination, AdminConfirmModal, Column } from '../../components/admin';

const CATEGORY_OPTIONS = [
  { label: 'All Categories', value: '' },
  { label: 'Functional', value: 'functional' },
  { label: 'Sectoral', value: 'sectoral' },
  { label: 'Valuation', value: 'valuation' },
  { label: 'Historical', value: 'historical' },
];

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  functional: { bg: '#3B82F620', text: '#3B82F6' },
  sectoral: { bg: '#8B5CF620', text: '#8B5CF6' },
  valuation: { bg: '#F59E0B20', text: '#F59E0B' },
  historical: { bg: '#10B98120', text: '#10B981' },
};

const AdminBuckets: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();
  const [buckets, setBuckets] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const pageSize = 20;

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ bucket_name: '', bucket_category: 'functional', stock_name: '', stock_symbol: '' });
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editBucket, setEditBucket] = useState<any>(null);
  const [editForm, setEditForm] = useState({ new_bucket_name: '', bucket_category: '' });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deletingBucket, setDeletingBucket] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (search) params.search = search;
    if (category) params.category = category;
    fetchAdminBuckets(params)
      .then((res) => { setBuckets(res.data.results); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, category]);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => { setPage(1); }, [search, category]);

  const handleCreate = async () => {
    if (!createForm.bucket_name || !createForm.stock_name) return;
    setCreating(true);
    try {
      await createAdminBucketStock(createForm);
      setShowCreate(false);
      setCreateForm({ bucket_name: '', bucket_category: 'functional', stock_name: '', stock_symbol: '' });
      load();
    } catch {
      alert('Failed to create bucket');
    }
    setCreating(false);
  };

  const handleEdit = async () => {
    if (!editBucket) return;
    setSaving(true);
    try {
      await updateAdminBucketMeta(editBucket.bucket_name, editForm);
      setEditBucket(null);
      load();
    } catch {
      alert('Failed to save changes');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deletingBucket) return;
    try {
      await deleteAdminBucket(deletingBucket);
      setDeletingBucket(null);
      load();
    } catch {
      alert('Failed to delete bucket');
    }
  };

  const columns: Column[] = [
    { key: 'bucket_name', header: 'Bucket Name' },
    {
      key: 'bucket_category', header: 'Category', width: '120px',
      render: (row) => {
        const c = CATEGORY_COLORS[row.bucket_category] || { bg: '#6B728020', text: '#6B7280' };
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: c.bg, color: c.text }}>
            {row.bucket_category || 'unknown'}
          </span>
        );
      },
    },
    { key: 'stock_count', header: 'Stocks', width: '80px' },
    {
      key: 'created_at', header: 'Created', width: '120px',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
    {
      key: 'actions', header: '', width: '140px',
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditBucket(row);
              setEditForm({ new_bucket_name: row.bucket_name, bucket_category: row.bucket_category || '' });
            }}
            className="px-2 py-1 rounded-lg text-xs font-medium"
            style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeletingBucket(row.bucket_name); }}
            className="px-2 py-1 rounded-lg text-xs font-medium"
            style={{ backgroundColor: '#EF444415', color: '#EF4444' }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const inputStyle = {
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.borderColor}`,
    color: colors.textColor,
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: colors.textColor }}>Bucket Management</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-xl text-sm font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          Create Bucket
        </button>
      </div>

      <div className="mb-4">
        <AdminSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search buckets..."
          filterOptions={CATEGORY_OPTIONS}
          filterValue={category}
          onFilterChange={setCategory}
        />
      </div>

      <AdminDataTable
        columns={columns}
        data={buckets}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/buckets/${encodeURIComponent(row.bucket_name)}`)}
        emptyMessage="No buckets found"
      />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Create Bucket</h3>
            <div className="space-y-3">
              <input
                placeholder="Bucket Name"
                value={createForm.bucket_name}
                onChange={(e) => setCreateForm({ ...createForm, bucket_name: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              />
              <select
                value={createForm.bucket_category}
                onChange={(e) => setCreateForm({ ...createForm, bucket_category: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              >
                {CATEGORY_OPTIONS.filter(o => o.value).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                placeholder="First Stock Name"
                value={createForm.stock_name}
                onChange={(e) => setCreateForm({ ...createForm, stock_name: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              />
              <input
                placeholder="Stock Symbol (optional)"
                value={createForm.stock_symbol}
                onChange={(e) => setCreateForm({ ...createForm, stock_symbol: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !createForm.bucket_name || !createForm.stock_name}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editBucket && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Edit Bucket</h3>
            <div className="space-y-3">
              <input
                placeholder="Bucket Name"
                value={editForm.new_bucket_name}
                onChange={(e) => setEditForm({ ...editForm, new_bucket_name: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              />
              <select
                value={editForm.bucket_category}
                onChange={(e) => setEditForm({ ...editForm, bucket_category: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              >
                {CATEGORY_OPTIONS.filter(o => o.value).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setEditBucket(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deletingBucket && (
        <AdminConfirmModal
          title="Delete Bucket"
          message={`Are you sure you want to delete "${deletingBucket}" and all its stocks? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          onConfirm={handleDelete}
          onCancel={() => setDeletingBucket(null)}
        />
      )}
    </div>
  );
};

export default AdminBuckets;
